import { fetchCategoryLevelData } from './dataApi';

const fetchConversationLevelData = (level, path, options) =>
  fetchCategoryLevelData('conversation', level, path, options);

const LEVELS = ['a1', 'a2', 'b1', 'b2'];

const levelRank = new Map([
  ['simple', 0],
  ['medium', 1],
  ['hard', 2],
  ['very_hard', 3],
]);

// Conversations are organized per CEFR level (a1/a2/b1/b2 — see
// conversation/{level}/catalog.json), each with 12 chapters at
// conversation/{level}/conversation{N}.json — an English-anchored root
// (wrapped as { conversation: {...} }) plus thin native/{code} overlays at
// conversation/{level}/native/{code}/conversation{N}.json. A locale's
// overlay may be a full re-authored chapter (schemaVersion, level, images,
// ...) or just title/scene/context/speakers/lines text — either way only
// the text fields below are read from it, so both shapes merge onto the
// English root identically (see mergeChapterOverlay). Requesting 'en' itself
// skips the overlay fetch and returns the root as-is.

// conversationId -> { level, path }, built once from every level's
// catalog.json. conversationId is globally unique across levels (see
// catalog.json's `id` field), even though `path` (conversationN.json)
// numbering restarts at 1 within each level.
let catalogIndexPromise = null;

async function fetchCatalogIndex() {
  if (!catalogIndexPromise) {
    catalogIndexPromise = (async () => {
      const index = new Map();
      for (const level of LEVELS) {
        try {
          const catalog = await fetchConversationLevelData(level, 'catalog.json');
          for (const entry of Object.values(catalog)) {
            if (!entry?.id || !entry?.path) continue;
            index.set(entry.id, { level, path: entry.path });
          }
        } catch {
          // This level's catalog isn't reachable yet — skip it rather than
          // losing every other level that did load.
        }
      }
      return index;
    })();
  }
  return catalogIndexPromise;
}

const rootChapterPromises = new Map();

function fetchRootChapter(level, path) {
  const key = `${level}/${path}`;
  if (!rootChapterPromises.has(key)) {
    rootChapterPromises.set(
      key,
      fetchConversationLevelData(level, path)
        .then((doc) => doc.conversation)
        .catch((error) => {
          rootChapterPromises.delete(key);
          throw error;
        }),
    );
  }
  return rootChapterPromises.get(key);
}

const overlayChapterPromises = new Map();

function fetchOverlayChapter(level, path, code) {
  if (!code || code === 'en') return Promise.resolve(null);
  const key = `${level}/${path}/${code}`;
  if (!overlayChapterPromises.has(key)) {
    overlayChapterPromises.set(
      key,
      fetchConversationLevelData(level, `native/${code}/${path}`)
        .then((doc) => doc.conversation)
        .catch(() => null),
    );
  }
  return overlayChapterPromises.get(key);
}

// title/scene/context, learningObjectives entries, speakers[].displayName,
// and line/segment text are authored as { txt, translate, translationHint }
// (same convention as topic-lessons/exam/basic-phrases-sentences), so
// unwrap once here rather than touching every call site below.
function unwrapText(value) {
  return value && typeof value === 'object' && 'txt' in value ? value.txt ?? null : value;
}

function unwrapChapter(chapter) {
  return {
    ...chapter,
    title: unwrapText(chapter.title),
    scene: unwrapText(chapter.scene),
    context: unwrapText(chapter.context),
    learningObjectives: (chapter.learningObjectives || []).map(unwrapText),
    speakers: (chapter.speakers || []).map((speaker) => ({
      ...speaker,
      displayName: unwrapText(speaker.displayName),
    })),
    lines: (chapter.lines || []).map((line) => ({
      ...line,
      text: unwrapText(line.text),
      segments: line.segments
        ? line.segments.map((segment) => ({ ...segment, text: unwrapText(segment.text) }))
        : line.segments,
    })),
  };
}

// Merges a reduced native overlay chapter (title/scene/context, speakers by
// id, lines by id — each holding just { txt }) onto the home's full chapter.
// Anything the overlay doesn't mention (structure, ids, level, images, ...)
// passes through from the base chapter unchanged. A no-op when the overlay
// code turns out to be a curriculum home itself (full shape) — the extra
// fields it carries (level, images, ...) are simply ignored here since the
// base chapter's own values already win for everything but the fields below.
function mergeChapterOverlay(baseChapter, overlayChapter) {
  if (!overlayChapter) return baseChapter;
  const speakersById = new Map((overlayChapter.speakers || []).map((s) => [s.id, s]));
  const linesById = new Map((overlayChapter.lines || []).map((l) => [l.id, l]));
  return {
    ...baseChapter,
    title: overlayChapter.title ?? baseChapter.title,
    scene: overlayChapter.scene ?? baseChapter.scene,
    context: overlayChapter.context ?? baseChapter.context,
    speakers: (baseChapter.speakers || []).map((speaker) => {
      const overlaySpeaker = speakersById.get(speaker.id);
      return overlaySpeaker
        ? { ...speaker, displayName: overlaySpeaker.displayName ?? speaker.displayName }
        : speaker;
    }),
    lines: (baseChapter.lines || []).map((line) => {
      const overlayLine = linesById.get(line.id);
      if (!overlayLine) return line;
      if (line.text !== undefined) {
        return { ...line, text: overlayLine.text ?? line.text };
      }
      if (line.segments) {
        return {
          ...line,
          segments: line.segments.map((segment, i) => {
            const overlaySegment = overlayLine.segments?.[i];
            return overlaySegment ? { ...segment, text: overlaySegment.text ?? segment.text } : segment;
          }),
        };
      }
      return line;
    }),
  };
}

function chapterToCategory(chapter) {
  const title = unwrapText(chapter.title);
  const scene = unwrapText(chapter.scene);
  const context = unwrapText(chapter.context);
  return {
    id: chapter.conversationId,
    chapterId: chapter.levelOrder,
    label: title || chapter.conversationId,
    title: title || '',
    scene: scene || '',
    context: context || '',
    images: chapter.images || [],
    level: chapter.level,
    levelOrder: chapter.levelOrder,
  };
}

function sortCategories(categories) {
  return [...categories].sort((left, right) => {
    const levelDifference = (levelRank.get(left.level) ?? 4) - (levelRank.get(right.level) ?? 4);
    return levelDifference || Number(left.levelOrder) - Number(right.levelOrder);
  });
}

// All 48 chapters (across every level) merged with `locale`'s native overlay
// for display — falls back to the English root's own text wherever `locale`
// (or its overlay fetch) doesn't cover a field. Passing locale: 'en' returns
// the root chapters as-is.
export async function loadConversationCategories(locale) {
  const catalogIndex = await fetchCatalogIndex();
  const categories = await Promise.all(
    Array.from(catalogIndex.entries()).map(async ([, { level, path }]) => {
      const root = await fetchRootChapter(level, path);
      const overlay = await fetchOverlayChapter(level, path, locale);
      return chapterToCategory(mergeChapterOverlay(root, overlay));
    }),
  );
  return sortCategories(categories);
}

export function mergeConversationCategories(targetCategories = [], nativeCategories = []) {
  const nativeById = new Map(nativeCategories.map((chapter) => [chapter.id, chapter]));
  return targetCategories.map((chapter) => {
    const nativeChapter = nativeById.get(chapter.id);
    return {
      ...chapter,
      nativeTitle: nativeChapter?.title || '',
      nativeScene: nativeChapter?.scene || '',
    };
  });
}

// One chapter, merged with `locale`'s native overlay for display.
export async function loadConversationDocument(locale, conversationId) {
  const catalogIndex = await fetchCatalogIndex();
  const location = catalogIndex.get(conversationId);
  if (!location) throw new Error(`Unknown conversation: ${conversationId}`);
  const { level, path } = location;
  const root = await fetchRootChapter(level, path);
  const overlay = await fetchOverlayChapter(level, path, locale);
  return unwrapChapter(mergeChapterOverlay(root, overlay));
}

export function conversationLineText(line, speakers = new Map()) {
  if (line.type === 'stage_direction') {
    return line.text ? `[${line.text}]` : '';
  }
  if (line.type !== 'dialogue') return line.text || '';

  const text = (line.segments || []).map((segment) => {
    if (segment.type === 'stage_direction') return segment.text ? `[${segment.text}]` : '';
    return segment.type === 'speech' ? segment.text || '' : '';
  }).filter(Boolean).join(' ').trim();
  const speaker = speakers.get(line.speakerId);
  return speaker && text ? `${speaker}: ${text}` : text;
}

export function conversationDocumentToLegacy(chapter) {
  const speakers = new Map(
    (chapter.speakers || []).map((speaker) => [speaker.id, speaker.displayName]),
  );
  const lines = (chapter.lines || []).map((line) => ({
    id: line.id,
    text: conversationLineText(line, speakers),
  })).filter((line) => line.text);
  return {
    chapterId: chapter.levelOrder || 0,
    conversationId: chapter.conversationId || '',
    title: chapter.title || '',
    scene: chapter.scene || '',
    context: chapter.context || '',
    content: Object.fromEntries(lines.map((line, index) => [String(index + 1), line.text])),
    lineIds: lines.map((line) => line.id),
    images: chapter.images || [],
  };
}

export async function loadConversationLegacyChapter(locale, conversationId) {
  return conversationDocumentToLegacy(await loadConversationDocument(locale, conversationId));
}

export function clearConversationV2Cache() {
  catalogIndexPromise = null;
  rootChapterPromises.clear();
  overlayChapterPromises.clear();
}
